import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Assessment as StatsIcon,
  DateRange as DateRangeIcon,
  TrendingUp as TrendingUpIcon,
  Message as MessageIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import { useMessagesService } from '../services/messages.service';

const MessageStatisticsDashboard = () => {
  const messagesService = useMessagesService();
  
  // State for query parameters
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('2025-01-01');
  const [endDate, setEndDate] = useState('2025-01-31');
  
  // State for results and UI
  const [statisticsData, setStatisticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Available periods
  const availablePeriods = [
    { value: 'day', label: 'Hoy' },
    { value: 'week', label: 'Esta Semana' },
    { value: 'month', label: 'Este Mes' },
    { value: 'custom', label: 'Rango Personalizado' }
  ];

  // Colors for charts
  const COLORS = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
    '#82CA9D', '#FFC658', '#FF7C7C', '#8DD1E1', '#D084D0',
    '#87D068'
  ];

  const executeStatisticsQuery = async () => {
    setLoading(true);
    setError(null);

    try {
      let result;
      if (period === 'custom') {
        result = await messagesService.getMessagesStatisticsCustomRange(startDate, endDate);
      } else {
        result = await messagesService.getMessagesStatistics(period);
      }
      
      setStatisticsData(result);
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Error al obtener estadísticas');
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Process data for charts
  const processDataForCharts = () => {
    if (!statisticsData?.statistics) return null;

    const templateData = statisticsData.statistics.map((stat, index) => ({
      name: stat.template_name,
      total_messages: stat.total_messages,
      unique_recipients: stat.unique_recipients,
      sent: stat.delivery_summary?.sent || 0,
      delivered: stat.delivery_summary?.delivered || 0,
      read: stat.delivery_summary?.read || 0,
      color: COLORS[index % COLORS.length]
    }));

    const deliveryData = statisticsData.statistics.map((stat, index) => ({
      name: stat.template_name.length > 15 ? stat.template_name.substring(0, 15) + '...' : stat.template_name,
      sent: stat.delivery_summary?.sent || 0,
      delivered: stat.delivery_summary?.delivered || 0,
      read: stat.delivery_summary?.read || 0,
    }));

    const topTemplatesData = statisticsData.statistics
      .slice(0, 10)
      .map((stat, index) => ({
        name: stat.template_name,
        value: stat.total_messages,
        color: COLORS[index % COLORS.length]
      }));

    return { templateData, deliveryData, topTemplatesData };
  };

  const chartData = processDataForCharts();

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-ES').format(num);
  };

  const calculateDeliveryRate = (stat) => {
    const total = stat.delivery_summary?.sent + stat.delivery_summary?.delivered + stat.delivery_summary?.read || 1;
    const delivered = stat.delivery_summary?.delivered + stat.delivery_summary?.read || 0;
    return ((delivered / total) * 100).toFixed(1);
  };

  const calculateReadRate = (stat) => {
    const total = stat.delivery_summary?.sent + stat.delivery_summary?.delivered + stat.delivery_summary?.read || 1;
    const read = stat.delivery_summary?.read || 0;
    return ((read / total) * 100).toFixed(1);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto', backgroundColor: '#f5f7fa' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, color: '#1a365d', fontWeight: 'bold' }}>
        📊 Dashboard de Estadísticas de Mensajes
      </Typography>
      
      {/* Query Parameters Panel */}
      <Card sx={{ mb: 3, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StatsIcon color="primary" />
            Parámetros de Consulta
          </Typography>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Período</InputLabel>
                <Select
                  value={period}
                  label="Período"
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  {availablePeriods.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {period === 'custom' && (
              <>
                <Grid item xs={12} md={2}>
                  <TextField
                    label="Fecha Inicio"
                    type="date"
                    size="small"
                    fullWidth
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label="Fecha Fin"
                    type="date"
                    size="small"
                    fullWidth
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
                onClick={executeStatisticsQuery}
                disabled={loading}
                size="large"
                sx={{
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                }}
              >
                {loading ? 'Cargando...' : 'Obtener Estadísticas'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Dashboard */}
      {statisticsData && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Total Mensajes
                      </Typography>
                      <Typography variant="h3" fontWeight="bold">
                        {formatNumber(statisticsData.summary?.total_messages || 0)}
                      </Typography>
                    </Box>
                    <MessageIcon sx={{ fontSize: 60, opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(240, 147, 251, 0.4)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Total Recipients
                      </Typography>
                      <Typography variant="h3" fontWeight="bold">
                        {formatNumber(statisticsData.summary?.total_recipients || 0)}
                      </Typography>
                    </Box>
                    <PeopleIcon sx={{ fontSize: 60, opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(79, 172, 254, 0.4)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Total Templates
                      </Typography>
                      <Typography variant="h3" fontWeight="bold">
                        {formatNumber(statisticsData.summary?.total_templates || 0)}
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 60, opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Top Templates Pie Chart */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: 400, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatsIcon color="primary" />
                    Top 10 Templates por Mensajes
                  </Typography>
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={chartData?.topTemplatesData || []}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${percent > 5 ? name.substring(0, 8) + '...' : ''} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData?.topTemplatesData?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value, name) => [formatNumber(value), 'Mensajes']} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Delivery Status Chart */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: 400, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="success" />
                    Estado de Entrega por Template
                  </Typography>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={chartData?.deliveryData?.slice(0, 8) || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        fontSize={10}
                      />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="sent" stackId="a" fill="#ff7c7c" name="Enviado" />
                      <Bar dataKey="delivered" stackId="a" fill="#ffc658" name="Entregado" />
                      <Bar dataKey="read" stackId="a" fill="#82ca9d" name="Leído" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Messages vs Recipients Chart */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Card sx={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon color="info" />
                    Mensajes vs Recipients por Template
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={chartData?.templateData || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        fontSize={10}
                      />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Area type="monotone" dataKey="total_messages" stackId="1" stroke="#8884d8" fill="#8884d8" name="Total Mensajes" />
                      <Area type="monotone" dataKey="unique_recipients" stackId="2" stroke="#82ca9d" fill="#82ca9d" name="Recipients Únicos" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Detailed Statistics Table */}
          <Card sx={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VisibilityIcon color="secondary" />
                Estadísticas Detalladas por Template
              </Typography>
              <List>
                {statisticsData.statistics?.map((stat, index) => (
                  <React.Fragment key={stat._id}>
                    <ListItem sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 2 }}>
                        <Typography variant="h6" color="primary">
                          {stat.template_name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip 
                            label={`${formatNumber(stat.total_messages)} mensajes`} 
                            color="primary" 
                            size="small" 
                          />
                          <Chip 
                            label={`${formatNumber(stat.unique_recipients)} recipients`} 
                            color="secondary" 
                            size="small" 
                          />
                        </Box>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                            <Typography variant="body2" color="text.secondary">Estado de Entrega</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" color="error">
                                  {formatNumber(stat.delivery_summary?.sent || 0)}
                                </Typography>
                                <Typography variant="caption">Enviado</Typography>
                              </Box>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" color="warning.main">
                                  {formatNumber(stat.delivery_summary?.delivered || 0)}
                                </Typography>
                                <Typography variant="caption">Entregado</Typography>
                              </Box>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" color="success.main">
                                  {formatNumber(stat.delivery_summary?.read || 0)}
                                </Typography>
                                <Typography variant="caption">Leído</Typography>
                              </Box>
                            </Box>
                          </Paper>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <Paper sx={{ p: 2, backgroundColor: '#e3f2fd' }}>
                            <Typography variant="body2" color="text.secondary">Tasa de Entrega</Typography>
                            <Typography variant="h4" color="info.main" sx={{ mt: 1 }}>
                              {calculateDeliveryRate(stat)}%
                            </Typography>
                          </Paper>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <Paper sx={{ p: 2, backgroundColor: '#e8f5e8' }}>
                            <Typography variant="body2" color="text.secondary">Tasa de Lectura</Typography>
                            <Typography variant="h4" color="success.main" sx={{ mt: 1 }}>
                              {calculateReadRate(stat)}%
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </ListItem>
                    {index < statisticsData.statistics.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Period Info */}
          {statisticsData.period_info && (
            <Card sx={{ mt: 3, backgroundColor: '#f0f4f8', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DateRangeIcon fontSize="small" />
                  <strong>Período:</strong> {statisticsData.period_info.period}
                  {statisticsData.period_info.start && statisticsData.period_info.end && (
                    <> | <strong>Desde:</strong> {statisticsData.period_info.start} <strong>hasta:</strong> {statisticsData.period_info.end}</>
                  )}
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* No Data Message */}
      {!loading && !statisticsData && !error && (
        <Card sx={{ textAlign: 'center', py: 8, backgroundColor: '#fafafa' }}>
          <CardContent>
            <StatsIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No hay datos disponibles
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Haz clic en "Obtener Estadísticas" para ver los datos
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MessageStatisticsDashboard;